import {
  Fit,
  Layout,
  useRive,
  useViewModel,
  useViewModelInstance,
  useViewModelInstanceEnum,
  useViewModelInstanceNumber,
  useViewModelInstanceString,
  ViewModelInstance,
} from '@rive-app/react-canvas';
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import dayRiv from '../../assets/rive-home/day.riv';
import daySnowRiv from '../../assets/rive-home/day-snow.riv';
import nightRiv from '../../assets/rive-home/night.riv';
import nightSnowRiv from '../../assets/rive-home/night-snow.riv';
import sunriseRiv from '../../assets/rive-home/sunrise.riv';
import sunriseSnowRiv from '../../assets/rive-home/sunrise-snow.riv';
import sunsetRiv from '../../assets/rive-home/sunset.riv';
import sunsetSnowRiv from '../../assets/rive-home/sunset-snow.riv';
import { TimeOfDay } from '../../utils/time';

const STATE_MACHINE = 'State Machine 1';
const IDLE = 0;
const PRECIPITATION_ON = 25;

export type PowerFlow = 'in' | 'out';

export type DeviceStatus = { powerFlow: PowerFlow } | { installInProgress: boolean };

export type DeviceKey =
  | 'solar'
  | 'evCharger'
  | 'tanklessWaterHeater'
  | 'waterHeater'
  | 'generator'
  | 'geothermal'
  | 'hvac'
  | 'battery'
  | 'grid'
  | 'inverter';

export type Devices = Partial<Record<DeviceKey, DeviceStatus>>;

export type Clouds = 'none' | 'partly-cloudy' | 'cloudy';

export type DeviceZoom = 'none' | 'liveStatus' | 'solar' | 'battery' | 'hvac' | 'charger' | 'generator';

export type PoweredDeviceKey = Extract<DeviceKey, 'solar' | 'evCharger' | 'generator' | 'battery' | 'grid' | 'inverter'>;

export type PowerLines = Partial<Record<PoweredDeviceKey, boolean>>;

export type RiveHomeProps = {
  snow: boolean;
  precipitation: boolean;
  lightning: boolean;
  clouds: Clouds;
  planning: boolean;
  solarObstructed: boolean;
  devices: Devices;
  powerLines?: PowerLines;
  deviceZoom?: DeviceZoom;
  isActive?: boolean;
};

export type RiveHomeViewProps = RiveHomeProps & {
  timeOfDay: TimeOfDay;
};

type NumberBinding =
  | 'lightning'
  | 'precipitation'
  | 'clouds'
  | 'solarPower'
  | 'inverterPower'
  | 'batteryPower'
  | 'generatorPower'
  | 'gridPower'
  | 'chargerPower';

type VisibilityBinding =
  | 'planningIsVisible'
  | 'solarObstructed'
  | 'solarIsVisible'
  | 'batteryIsVisible'
  | 'generatorIsVisible'
  | 'chargerIsVisible'
  | 'hvacIsVisible'
  | 'tankedIsVisible'
  | 'tanklessIsVisible'
  | 'geothermIsVisible'
  | 'solarProgIsVisible'
  | 'batteryProgIsVisible'
  | 'generatorProgIsVisible'
  | 'chargerProgIsVisible'
  | 'hvacProgIsVisible'
  | 'tankedProgIsVisible'
  | 'solarPowerIsVisible'
  | 'inverterPowerIsVisible'
  | 'batteryPowerIsVisible'
  | 'generatorPowerIsVisible'
  | 'gridPowerIsVisible'
  | 'chargerPowerIsVisible';

type LayoutDisplay = 'Show' | 'Hide';

const CLOUDS_VALUES: Record<Clouds, number> = {
  none: 0,
  'partly-cloudy': 1,
  cloudy: 2,
};

const POWER_FLOW_VALUES: Record<PoweredDeviceKey, Partial<Record<PowerFlow, number>>> = {
  solar: { out: 1 },
  evCharger: { in: 1 },
  generator: { out: 1, in: 2 },
  battery: { out: 1, in: 2 },
  grid: { out: 1, in: 2 },
  inverter: { out: 1, in: 2 },
};

const getPowerValue = (device: PoweredDeviceKey, status?: DeviceStatus): number =>
  status && 'powerFlow' in status ? POWER_FLOW_VALUES[device][status.powerFlow] ?? IDLE : IDLE;

const isInstallInProgress = (status?: DeviceStatus): boolean =>
  status !== undefined && 'installInProgress' in status && status.installInProgress;

const RIVE_HOME_SRCS: Record<TimeOfDay, { clear: string; snow: string }> = {
  day: { clear: dayRiv, snow: daySnowRiv },
  sunrise: { clear: sunriseRiv, snow: sunriseSnowRiv },
  sunset: { clear: sunsetRiv, snow: sunsetSnowRiv },
  night: { clear: nightRiv, snow: nightSnowRiv },
};

export const getRiveHomeSrc = (timeOfDay: TimeOfDay, snow: boolean): string =>
  RIVE_HOME_SRCS[timeOfDay][snow ? 'snow' : 'clear'];

export const riveHomeBufferQuery = (src: string) => ({
  queryKey: ['rive-home', src],
  queryFn: () => fetch(src).then((response) => response.arrayBuffer()),
  staleTime: Infinity,
  cacheTime: Infinity,
});

const useNumberBinding = (path: NumberBinding, value: number, viewModelInstance: ViewModelInstance | null) => {
  const { setValue } = useViewModelInstanceNumber(path, viewModelInstance);

  useEffect(() => {
    setValue(value);
  }, [setValue, value]);
};

const useVisibilityBinding = (
  path: VisibilityBinding,
  visible: boolean,
  viewModelInstance: ViewModelInstance | null,
) => {
  const { setValue } = useViewModelInstanceEnum(path, viewModelInstance);

  useEffect(() => {
    const display: LayoutDisplay = visible ? 'Show' : 'Hide';
    setValue(display);
  }, [setValue, visible]);
};

type RiveHomeSceneProps = Omit<RiveHomeViewProps, 'timeOfDay' | 'snow'> & {
  buffer: ArrayBuffer;
};

const RiveHomeScene = ({
  buffer,
  precipitation,
  lightning,
  clouds,
  planning,
  solarObstructed,
  devices,
  powerLines = {},
  deviceZoom = 'none',
  isActive = true,
}: RiveHomeSceneProps) => {
  const { RiveComponent, rive } = useRive(
    {
      buffer,
      stateMachines: STATE_MACHINE,
      autoplay: true,
      autoBind: true,
      layout: new Layout({ fit: Fit.Cover }),
    },
    { shouldResizeCanvasToContainer: true },
  );
  const vm = useViewModel(rive);
  const vmi = useViewModelInstance(vm, { rive });

  useNumberBinding('lightning', Number(lightning), vmi);
  useNumberBinding('precipitation', precipitation ? PRECIPITATION_ON : 0, vmi);
  useNumberBinding('clouds', CLOUDS_VALUES[clouds], vmi);
  useNumberBinding('solarPower', getPowerValue('solar', devices.solar), vmi);
  useNumberBinding('inverterPower', getPowerValue('inverter', devices.inverter), vmi);
  useNumberBinding('batteryPower', getPowerValue('battery', devices.battery), vmi);
  useNumberBinding('generatorPower', getPowerValue('generator', devices.generator), vmi);
  useNumberBinding('gridPower', getPowerValue('grid', devices.grid), vmi);
  useNumberBinding('chargerPower', getPowerValue('evCharger', devices.evCharger), vmi);

  useVisibilityBinding('planningIsVisible', planning, vmi);
  useVisibilityBinding('solarObstructed', solarObstructed, vmi);
  useVisibilityBinding('solarIsVisible', devices.solar !== undefined, vmi);
  useVisibilityBinding('batteryIsVisible', devices.battery !== undefined, vmi);
  useVisibilityBinding('generatorIsVisible', devices.generator !== undefined, vmi);
  useVisibilityBinding('chargerIsVisible', devices.evCharger !== undefined, vmi);
  useVisibilityBinding('hvacIsVisible', devices.hvac !== undefined, vmi);
  useVisibilityBinding('tankedIsVisible', devices.waterHeater !== undefined, vmi);
  useVisibilityBinding('tanklessIsVisible', devices.tanklessWaterHeater !== undefined, vmi);
  useVisibilityBinding('geothermIsVisible', devices.geothermal !== undefined, vmi);
  useVisibilityBinding('solarProgIsVisible', isInstallInProgress(devices.solar), vmi);
  useVisibilityBinding('batteryProgIsVisible', isInstallInProgress(devices.battery), vmi);
  useVisibilityBinding('generatorProgIsVisible', isInstallInProgress(devices.generator), vmi);
  useVisibilityBinding('chargerProgIsVisible', isInstallInProgress(devices.evCharger), vmi);
  useVisibilityBinding('hvacProgIsVisible', isInstallInProgress(devices.hvac), vmi);
  useVisibilityBinding('tankedProgIsVisible', isInstallInProgress(devices.waterHeater), vmi);
  useVisibilityBinding('solarPowerIsVisible', powerLines.solar ?? true, vmi);
  useVisibilityBinding('inverterPowerIsVisible', powerLines.inverter ?? true, vmi);
  useVisibilityBinding('batteryPowerIsVisible', powerLines.battery ?? true, vmi);
  useVisibilityBinding('generatorPowerIsVisible', powerLines.generator ?? true, vmi);
  useVisibilityBinding('gridPowerIsVisible', powerLines.grid ?? true, vmi);
  useVisibilityBinding('chargerPowerIsVisible', powerLines.evCharger ?? true, vmi);

  const { setValue: setDeviceZoom } = useViewModelInstanceString('deviceZoom', vmi);

  useEffect(() => {
    setDeviceZoom(deviceZoom);
  }, [setDeviceZoom, deviceZoom]);

  useEffect(() => {
    if (isActive) {
      rive?.play();
    } else {
      rive?.pause();
    }
  }, [rive, isActive]);

  return <RiveComponent className="h-full w-full" />;
};

export const RiveHomeView = ({ timeOfDay, snow, ...sceneProps }: RiveHomeViewProps) => {
  const src = getRiveHomeSrc(timeOfDay, snow);
  const { data: buffer } = useQuery(riveHomeBufferQuery(src));
  return buffer ? <RiveHomeScene key={src} buffer={buffer} {...sceneProps} /> : null;
};

export default RiveHomeView;
